// src/components/Header.jsx
import {
  Box,
  Flex,
  Button,
  Stack,
  Container,
  Heading,
  IconButton,
  useDisclosure,
  HStack,
  useColorModeValue,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Avatar,
  Text,
  MenuDivider,
} from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import { HamburgerIcon, CloseIcon, ChevronDownIcon } from '@chakra-ui/icons';
import { useApi } from '@/lib/api';
import useAuthStore from '@/stores/authStore';

const Header = () => {
  const user = useAuthStore((state) => state.user);
  const isLoggedIn = useAuthStore((state) => state.isLoggedIn());
  const clearUser = useAuthStore((state) => state.clearUser);

  const { isOpen, onToggle, onClose } = useDisclosure();
  const navigate = useNavigate();

  const isAuthenticated = isLoggedIn;

  const colorValue = useColorModeValue('green.50', 'brand.bgDarker');

  const handleNavigation = (to) => {
    onClose(); // Close the mobile menu
    navigate(to);
  };

  const { request } = useApi();

  const handleLogout = async () => {
    try {
      await request({
        method: 'POST',
        url: '/api/auth/logout',
        successMessage: 'Logged out successfully',
      });
      clearUser();
      navigate('/');
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  // eslint-disable-next-line react/prop-types
  const NavLink = ({ to, children }) => (
    <Button
      variant="ghost"
      fontWeight="medium"
      onClick={() => handleNavigation(to)}
    >
      {children}
    </Button>
  );

  const UserMenu = () => (
    <Menu>
      <MenuButton
        as={Button}
        variant="ghost"
        rounded="full"
        cursor="pointer"
        minW={0}
      >
        <HStack spacing={2}>
          <Avatar size="sm" src={user?.picture} name={user?.name} />
          <Text display={{ base: 'none', md: 'block' }}>{user?.name}</Text>
          <ChevronDownIcon />
        </HStack>
      </MenuButton>
      <MenuList
        bg={useColorModeValue('white', 'brand.bgDark')}
        borderColor={useColorModeValue('gray.200', 'brand.bgDarker')}
      >
        <MenuItem
          onClick={() => handleNavigation('/user-profile')}
          _hover={{
            bg: colorValue,
          }}
        >
          User Profile
        </MenuItem>
        {(user?.role === 'EXPERT' || user?.role === 'ADMIN') && (
          <MenuItem
            onClick={() => handleNavigation(`/expert/${user?.slug || user?.id}`)}
            _hover={{
              bg: colorValue,
            }}
          >
            Expert Profile
          </MenuItem>
        )}
        {/* Add link for Admin Dashboard if user is an admin */}
        {user?.role === 'ADMIN' && (
          <MenuItem
            onClick={() => handleNavigation('/admin')}
            _hover={{
              bg: colorValue,
            }}
          >
            Admin Dashboard
          </MenuItem>
        )}
        {/* Add link to Host Upcoming Sessions if user is an expert or admin */}
        {(user?.role === 'EXPERT' || user?.role === 'ADMIN') && (
          <>
            <MenuItem
              onClick={() => handleNavigation('/host/sessions')}
              _hover={{
                bg: colorValue,
              }}
            >
              My Host Sessions
            </MenuItem>
          </>
        )}
        {/* Add link for Guest Upcoming Sessions (visible to all logged-in users) */}
        <MenuItem
          onClick={() => handleNavigation('/my-sessions')}
          _hover={{
            bg: colorValue,
          }}
        >
          My Guest Sessions
        </MenuItem>

        <MenuDivider />
        <MenuItem
          onClick={handleLogout}
          _hover={{
            bg: useColorModeValue('red.50', 'red.900'),
            color: useColorModeValue('red.600', 'red.200'),
          }}
        >
          Sign Out
        </MenuItem>
      </MenuList>
    </Menu>
  );

  return (
    <Box
      bg={useColorModeValue('white', 'brand.bgDarker')}
      px={4}
      boxShadow="sm"
      position="fixed"
      width="full"
      zIndex="sticky"
    >
      <Container maxW="container.xl">
        <Flex h={16} alignItems="center" justifyContent="space-between">
          <IconButton
            size="md"
            icon={isOpen ? <CloseIcon /> : <HamburgerIcon />}
            aria-label="Open Menu"
            display={{ md: 'none' }}
            onClick={onToggle}
          />

          <HStack spacing={8} alignItems="center">
            <Heading
              size="md"
              color={useColorModeValue('brand.success', 'brand.successLight')}
              cursor="pointer"
              onClick={() => handleNavigation('/')}
            >
              Conversify
            </Heading>
            <HStack as="nav" spacing={4} display={{ base: 'none', md: 'flex' }}>
              <NavLink to="/features">Features</NavLink>
              <NavLink to="/pricing">Pricing</NavLink>
              <NavLink to="/experts">Experts</NavLink>
            </HStack>
          </HStack>

          <Flex alignItems="center">
            <Stack direction="row" spacing={7} alignItems="center">
              {/* <IconButton
                size="md"
                icon={colorMode === "light" ? <MoonIcon /> : <SunIcon />}
                aria-label="Toggle color mode"
                onClick={toggleColorMode}
                variant="ghost"
              /> */}

              {isAuthenticated ? (
                <UserMenu />
              ) : (
                <>
                  <Button
                    display={{ base: 'none', md: 'inline-flex' }}
                    variant="ghost"
                    fontSize="sm"
                    fontWeight={600}
                    onClick={() => handleNavigation('/signin')}
                  >
                    Sign In
                  </Button>
                  <Button
                    display={{ base: 'none', md: 'inline-flex' }}
                    fontSize="sm"
                    fontWeight={600}
                    bg="brand.success"
                    color="white"
                    onClick={() => handleNavigation('/signup')}
                    _hover={{
                      bg: 'brand.successDark',
                    }}
                  >
                    Get Started
                  </Button>
                </>
              )}
            </Stack>
          </Flex>
        </Flex>

        {/* Mobile menu */}
        {isOpen && (
          <Box pb={4} display={{ md: 'none' }}>
            <Stack as="nav" spacing={4}>
              <NavLink to="/features">Features</NavLink>
              <NavLink to="/pricing">Pricing</NavLink>
              <NavLink to="/experts">Experts</NavLink>
              {!isAuthenticated && (
                <>
                  <Button
                    fontSize="sm"
                    fontWeight={600}
                    variant="ghost"
                    w="full"
                    onClick={() => handleNavigation('/signin')}
                  >
                    Sign In
                  </Button>
                  <Button
                    fontSize="sm"
                    fontWeight={600}
                    bg="brand.success"
                    color="white"
                    w="full"
                    onClick={() => handleNavigation('/signup')}
                    _hover={{
                      bg: 'brand.successDark',
                    }}
                  >
                    Get Started
                  </Button>
                </>
              )}
            </Stack>
          </Box>
        )}
      </Container>
    </Box>
  );
};

export default Header;
